import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  lockScroll,
  unlockScroll,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * WCS shop nudge (2026-10-15): one photo at a time at every width, with previous and next
 * buttons, the arrow keys, a swipe on a phone (the `wcs:zoom-swipe` event of
 * `drag-zoom-wrapper`), a photo counter, a locked page scroll, and the focus back on the
 * gallery photo of the last shown photo when the dialog closes. See `snippets/wcs-product-zoom.liquid`.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [counter] - The visible photo counter.
 * @property {HTMLElement} [status] - The live region that tells a screen reader the photo number.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** The index of the shown photo. */
  #index = 0;

  /** True when the visitor used the keyboard last: then the focus ring shows after close. */
  #keyboard = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
    this.refs.dialog.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('wcs:zoom-swipe', this.#handleSwipe);
  }

  #handlePointerDown = () => {
    this.#keyboard = false;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);
    this.refs.dialog.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('wcs:zoom-swipe', this.#handleSwipe);
    unlockScroll(this.refs.dialog);
  }

  /** Shows the next photo, after the last one the first. */
  next() {
    this.#go(this.#index + 1);
  }

  /** Shows the previous photo, before the first one the last. */
  previous() {
    this.#go(this.#index - 1);
  }

  /**
   * @param {number} index - The photo to show; it wraps around.
   */
  #go(index) {
    const count = this.refs.media.length;
    if (count < 2) return;

    const target = (index + count) % count;
    // A jump over more than one photo (the wrap from the last to the first) is instant, so
    // the visitor does not see all the photos go past.
    const far = Math.abs(target - this.#index) > 1;
    const behavior = prefersReducedMotion() || far ? 'instant' : 'smooth';
    this.selectThumbnail(target, { behavior });
  }

  /**
   * Scrolls the dialog's photo list, and nothing else, to a photo.
   * @param {HTMLElement | undefined} media - The photo.
   * @param {ScrollBehavior} behavior - The scroll behaviour.
   */
  #scrollToMedia(media, behavior) {
    const list = media?.parentElement;
    if (!media || !list) return;

    scrollIntoView(media, { ancestor: list, behavior, block: 'start', inline: 'start' });
  }

  /**
   * @param {CustomEvent<{ direction: number }>} event - The swipe of `drag-zoom-wrapper`.
   */
  #handleSwipe = (event) => {
    this.#go(this.#index + event.detail.direction);
  };

  /**
   * Keeps the shown photo, the counter and the screen reader text in step.
   * @param {number} index - The index of the shown photo.
   */
  #setIndex(index) {
    const { media, counter, status } = this.refs;
    const changed = index !== this.#index;
    this.#index = index;

    for (const wrapper of this.querySelectorAll('drag-zoom-wrapper')) {
      if (changed && 'reset' in wrapper && typeof wrapper.reset === 'function') wrapper.reset();
    }

    if (counter) counter.textContent = `${index + 1} / ${media.length}`;
    if (status) status.textContent = (status.dataset.template ?? '').replace('[index]', `${index + 1}`);
  }

  /**
   * Unlocks the page and puts the focus on the gallery photo of the last shown photo. The
   * native dialog gives the focus back to the element that opened it; this runs after that.
   */
  #handleDialogClose = () => {
    unlockScroll(this.refs.dialog);
    if (this.refs.status) this.refs.status.textContent = '';

    // The gallery shows the photo a few frames later (its slide stops being aria-hidden), so try
    // again for some frames.
    const gallery = this.closest('media-gallery');
    const index = this.#index;
    const quiet = !this.#keyboard;
    let tries = 20;
    const focusPhoto = () => {
      const button = gallery?.querySelector(`.wcs-zoom-open[data-zoom-index="${index}"]`);
      if (!(button instanceof HTMLElement) || this.refs.dialog.open) return;
      if (quiet) {
        button.classList.add('wcs-zoom-open--quiet');
        button.addEventListener('blur', () => button.classList.remove('wcs-zoom-open--quiet'), { once: true });
      }
      button.focus({ preventScroll: true });
      if (document.activeElement !== button && --tries > 0) requestAnimationFrame(focusPhoto);
    };
    requestAnimationFrame(focusPhoto);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();
    // A click from the keyboard (Enter or Space on the photo button) has no pointer detail.
    this.#keyboard = event.detail === 0;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // The dialog itself takes the first focus: a screen reader reads its name, the arrow keys
      // work at once, and no focus ring shows on the close button after a click.
      dialog.focus({ preventScroll: true });
      lockScroll(dialog);
      this.#setIndex(index);

      // Scroll only the dialog's own lists: an element's scrollIntoView also scrolls the page
      // behind the dialog, and the page then is not where the visitor left it.
      this.#scrollToMedia(targetImage, 'instant');
      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, { ancestor: thumbnails, behavior: 'instant', block: 'center', inline: 'center' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    this.#keyboard = true;

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      if (event.key === 'ArrowRight') this.next();
      else this.previous();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setIndex(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      this.#scrollToMedia(targetImage, options.behavior);

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
